import type { ReviewSourceId, WidgetType } from '@flexbyt/shared';

/**
 * Who ReviewsJet is actually for, said as businesses rather than as features.
 *
 * ===========================================================================
 * The question this answers, and the query it answers it for
 * ===========================================================================
 * Every other page on this site is organised by *mechanism* — by which platform
 * the reviews come from, by which layout they end up in, by which website
 * builder the customer uses. That is the right way to organise a product and
 * the wrong way to meet somebody who has not decided they want one yet.
 *
 * A restaurant owner does not search for "review aggregation widget". They
 * search "how to show google reviews on my restaurant website", and every page
 * we had answered a different half of that sentence: `/integrations/google-reviews`
 * knows about Google and nothing about restaurants, `/widgets/review-masonry`
 * knows about walls of cards and nothing about either. This is the page that
 * takes the whole sentence, and its job is to let somebody find themselves in a
 * list and then be sent to the two pages that actually do the work.
 *
 * ===========================================================================
 * What each entry has to earn
 * ===========================================================================
 * An industries page is the easiest page on a marketing site to pad. Twelve
 * cards reading "Dentists — show your dental practice reviews and win more
 * dental patients" is a page that ranks for nothing and insults everybody on
 * it, and it is what you get when the only thing that varies between entries is
 * a substituted noun.
 *
 * So every field below has to say something that is only true of this kind of
 * business:
 *
 *   `where`     which platforms these people's reviews are *already* on. This
 *               is the fact that makes the page useful rather than flattering:
 *               a hotel's reviews are on Tripadvisor and a tradesperson's are
 *               on Google, and telling somebody which door is theirs saves them
 *               the whole "which of these five do I need" question.
 *   `problem`   the specific reason their reviews are not on their site today.
 *               Not "social proof increases conversions" — the actual, small,
 *               true obstacle.
 *   `widget`    which layout suits their page, and the reason. A restaurant
 *               with a one-page site needs a different shape from a law firm
 *               with a services page.
 *
 * `sources` is typed as `ReviewSourceId[]` and `widget` as `WidgetType`, so an
 * entry cannot name a platform ReviewsJet does not import from or a layout it
 * does not ship — the two errors this page would otherwise make invisibly.
 */

export type IndustryContent = {
  /**
   * The URL segment, and the anchor on the hub: `/who-its-for/restaurants`.
   *
   * The trade's own word rather than the search phrase. `/who-its-for/how-to-
   * show-google-reviews-on-a-restaurant-website` would carry more of the query
   * and read as a page written for a crawler — which is the impression that
   * costs a click at the moment somebody looks at the address bar. The phrase
   * belongs in the title and the h1, where a person reads it.
   */
  id: string;
  /** What these people call themselves, in the plural. */
  name: string;
  /** The phrase somebody in this trade would type. Used in copy, not stuffed. */
  searchPhrase: string;
  /** Platforms their reviews are already on, most likely first. */
  sources: ReviewSourceId[];
  /** Why their reviews are not on their own site today. One sentence. */
  problem: string;
  /** What changes once they are. One sentence, concrete. */
  outcome: string;
  /** The layout that suits their page, and why in half a sentence. */
  widget: WidgetType;
  widgetReason: string;

  /*
    ---------------------------------------------------------------------------
    The page of its own
    ---------------------------------------------------------------------------
    Everything above is what the hub row shows. Everything below exists because
    the hub row is not a page: it answers "is this for me" in four lines, and
    somebody who says yes then has three more questions — what exactly happens,
    what will it look like on *my* kind of page, and what about the thing my
    trade always worries about.

    These fields are written per industry and are checked for distinctness by
    `industries.test.ts`. Ten pages differing by a substituted noun are the thin
    programmatic pages the brief rules out, and they read as thin to a person
    long before a search engine decides anything — so each `point` and each
    `faq` below has to be something that is only true of this trade.
  */
  /** The `<title>`. Carries the phrase people actually type. */
  seoTitle: string;
  /** The meta description. One sentence of what, one of how. */
  seoDescription: string;
  /** The h1, and the run of words in it worth marking. */
  headline: string;
  markPhrase: string;
  /** Two sentences under the h1. */
  intro: string;
  /** Three things that are true of this trade and not of the others. */
  points: { title: string; body: string }[];
  /** The questions this trade actually asks, answered honestly. */
  faqs: { q: string; a: string }[];
};

export const INDUSTRIES: IndustryContent[] = [
  {
    id: 'trades',
    name: 'Trades and home services',
    searchPhrase: 'show Google reviews on a plumber or electrician website',
    sources: ['google'],
    problem:
      'Almost all of it is on Google, where somebody has to already be searching for you to see it. The website — the one you paid for and send people to — shows none of it.',
    outcome:
      'The reviews that win you the job appear next to your phone number, so the person deciding between three quotes reads them without leaving your page.',
    widget: 'slider',
    widgetReason:
      'A carousel sits in a fixed height above the contact form, so a homepage full of reviews does not push the number below the fold.',
    seoTitle: 'Show Google reviews on a tradesperson\'s website',
    seoDescription:
      'Plumbers, electricians, builders and roofers: bring your Google reviews onto your own site so the person comparing three quotes reads them next to your phone number. Free plan, no card.',
    headline: 'The quote you win is the one with reviews next to it.',
    markPhrase: 'reviews next to it',
    intro:
      'Homeowners get three quotes and pick on trust, not on price alone. Your Google reviews are the trust — and right now they only exist on Google, where somebody has to already be searching for you to find them.',
    points: [
      {
        title: 'Your reviews arrive faster than your quote',
        body: 'Most trades quote within a day or two. In that gap the homeowner is looking you up, and what they find on your own site should not be a blank page where the proof ought to be.',
      },
      {
        title: 'One listing, however many vans',
        body: 'A source is a listing, not a company. If you have a Google profile per town you cover, connect each one — they all land in the same library and you choose which reviews go where.',
      },
      {
        title: 'It keeps up without you',
        body: 'Nobody in a trade has time to copy reviews into a website. ReviewsJet re-checks your listing and new reviews appear on the site on their own, published or held back depending on how you set it.',
      },
    ],
    faqs: [
      {
        q: 'I only have a Google Business Profile. Is that enough?',
        a: 'Yes, and it is what most trades have. Paste the link to your listing and that is the whole setup — Google is one of the five platforms ReviewsJet reads directly, with no plugin and no extension.',
      },
      {
        q: 'Can I leave out the one bad review?',
        a: 'Yes. Every review arrives unpublished and nothing appears on your site until you publish it. We would say a four-star with a fair complaint often reads better than a wall of fives, but it is your call, not ours.',
      },
      {
        q: 'My site was built by someone else and I cannot edit it.',
        a: 'You need one line of HTML pasted once. If your site is on Wix or Webflow there is a ReviewsJet app instead, and on WordPress a plugin — in all three cases somebody can do it in a couple of minutes without touching the design.',
      },
    ],
  },
  {
    id: 'hospitality',
    name: 'Hotels, B&Bs and holiday lets',
    searchPhrase: 'put Tripadvisor and Airbnb reviews on a hotel website',
    sources: ['tripadvisor', 'google', 'airbnb'],
    problem:
      'The reviews are spread across Tripadvisor, Google and Airbnb, and the guest reading your own site sees whichever one they happened to arrive from — or none.',
    outcome:
      'One reviews page carrying every platform at once, so a direct booking is made with the same evidence an OTA booking would have been.',
    widget: 'masonry',
    widgetReason:
      'Guest reviews vary from one line to six and often carry photos; masonry packs both without leaving gaps.',
    seoTitle: 'Put TripAdvisor and Google reviews on your hotel website',
    seoDescription:
      'Hotels, B&Bs and guesthouses: show your TripAdvisor, Google and Airbnb reviews together on your own site, so a direct booking is made with the same evidence an OTA booking would have.',
    headline: 'Direct bookings deserve the same proof the OTAs get.',
    markPhrase: 'the same proof the OTAs get',
    intro:
      'Booking.com and TripAdvisor put hundreds of reviews next to your room rate. Your own site — the booking that costs you no commission — usually shows a handful of hand-picked quotes, or none.',
    points: [
      {
        title: 'Every platform on one page',
        body: 'Guests review you wherever they booked, so your proof is split three or four ways. One reviews page carrying TripAdvisor, Google and Airbnb at once is something no single OTA profile can show.',
      },
      {
        title: 'Photographs are half of it',
        body: 'Guest reviews come with pictures of the room, the breakfast, the courtyard — and those photos do more for a booking than the sentence beside them. Masonry sizes each card around its image instead of cropping it.',
      },
      {
        title: 'A review per property',
        body: 'Running two guesthouses, or rooms plus a holiday let? Connect a listing each and tag them, and each property\'s page shows only its own reviews rather than an average of both.',
      },
    ],
    faqs: [
      {
        q: 'Will this affect my TripAdvisor ranking?',
        a: 'No. ReviewsJet reads your public listing and displays it on your own site; it does not post, reply or interact with TripAdvisor in any way, so there is nothing for their ranking to react to.',
      },
      {
        q: 'Can I show the overall score as well as the reviews?',
        a: 'Yes — the widget draws a summary card with your average and the number of reviews, and you can turn it off if you would rather the reviews spoke for themselves.',
      },
      {
        q: 'We get reviews in several languages. What happens to those?',
        a: 'They come across as written. There is no translation, which is the honest behaviour — a machine-translated guest review is no longer that guest\'s words, and a German traveller reading a German review is a better outcome anyway.',
      },
    ],
  },
  {
    id: 'restaurants',
    name: 'Restaurants, cafés and bars',
    searchPhrase: 'add Google reviews to a restaurant website',
    sources: ['google', 'tripadvisor', 'yelp'],
    problem:
      'A restaurant site is usually one page — menu, hours, a booking link — and there is no room for a reviews section without pushing the menu down.',
    outcome:
      'A band of reviews between the menu and the booking button, updating itself every time somebody leaves one after dinner.',
    widget: 'marquee',
    widgetReason:
      'A ticker is a full-width strip rather than a block, so it fits a one-page site without costing it a screenful.',
    seoTitle: 'Add Google reviews to a restaurant website',
    seoDescription:
      'Restaurants, cafés and bars: put your Google, TripAdvisor and Yelp reviews on your own site without pushing the menu down the page. Free plan, one line of code.',
    headline: 'Your menu deserves the page. Your reviews deserve a band.',
    markPhrase: 'deserve a band',
    intro:
      'A restaurant site is usually one page — menu, hours, a booking link — and there is nowhere to put a reviews section without shoving the menu below the fold. That is why most restaurants have none.',
    points: [
      {
        title: 'A strip, not a section',
        body: 'The marquee is a full-width band about the height of a paragraph. It slots between the menu and the booking button and costs you no scroll depth, which is the reason a reviews section usually gets cut.',
      },
      {
        title: 'Reviews arrive after dinner',
        body: 'Restaurant reviews are written the same evening or the next morning, in a steady trickle. A widget that re-checks the listing means last night\'s five stars is on the site by lunchtime with nobody doing anything.',
      },
      {
        title: 'Google, TripAdvisor and Yelp together',
        body: 'Diners review on whichever app they opened. Rather than picking the platform where you happen to look best, show all three and let the volume make the argument.',
      },
    ],
    faqs: [
      {
        q: 'Will it slow down my site?',
        a: 'It loads after your page does and fetches nothing until then, so your menu and your booking link paint first. The script is a few kilobytes.',
      },
      {
        q: 'Can I show only the reviews that mention the food?',
        a: 'Not by keyword. You can tag reviews yourself and limit a widget to a tag, which is the same outcome with you deciding rather than a keyword match deciding — worth the few minutes it takes.',
      },
      {
        q: 'What about the one-star from someone who never came in?',
        a: 'Leave it unpublished. Nothing reaches your site until you publish it, and you can unpublish later without deleting anything.',
      },
    ],
  },
  {
    id: 'ecommerce',
    name: 'Online shops',
    searchPhrase: 'display Etsy or Amazon reviews on my own store',
    sources: ['etsy', 'amazon', 'aliexpress', 'google'],
    problem:
      'The reviews that took years to earn belong to the marketplace, and a shopper on your own store has no way of knowing they exist.',
    outcome:
      'Marketplace reviews on your own product and landing pages, which is the whole argument for buying direct rather than through the platform.',
    widget: 'grid',
    widgetReason:
      'Product reviews tend to be a similar length, and a tidy grid lines up with the rest of a shop page.',
    seoTitle: 'Show Etsy and Amazon reviews on your own online store',
    seoDescription:
      'Take the reviews you earned on marketplaces and display them on your own store, where the sale is worth more. Etsy, Amazon and AliExpress reviews via the free Chrome extension.',
    headline: 'You earned those reviews. The marketplace keeps them.',
    markPhrase: 'The marketplace keeps them',
    intro:
      'Years of five-star feedback sits on Etsy or Amazon, doing nothing for the store where your margin is. A shopper on your own site has no way of knowing any of it exists.',
    points: [
      {
        title: 'The argument for buying direct',
        body: 'A shopper who found you on a marketplace and then landed on your own store is asking one question: is this the same seller. Your marketplace reviews on your own product page answer it in a glance.',
      },
      {
        title: 'Reviews per product, not per shop',
        body: 'Tag reviews by the product they are about and limit each product page\'s widget to that tag, so a listing shows its own feedback rather than an average of everything you sell.',
      },
      {
        title: 'A grid, because your reviews are uniform',
        body: 'Marketplace reviews are short and similar in length. A tidy grid lines up with the rest of a shop page; masonry\'s staggered columns only earn their keep when the reviews are uneven.',
      },
    ],
    faqs: [
      {
        q: 'Why do Etsy and Amazon need a browser extension?',
        a: 'Their review pages cannot be read from a server the way Google\'s and TripAdvisor\'s can. The free ReviewsJet Chrome extension collects them in your own browser, on your own account, while you are signed in — which is a different mechanism, and we would rather say so than pretend it is the same.',
      },
      {
        q: 'Does it keep syncing like the Google ones do?',
        a: 'No. Extension sources are collected when you run them, so new marketplace reviews arrive when you next open the extension. The five direct sources refresh on their own.',
      },
      {
        q: 'Is showing marketplace reviews on my own site allowed?',
        a: 'You are displaying public reviews of your own business, with the reviewer\'s name and the platform they were written on shown. That is the same thing every review widget does. We are not lawyers, and if your marketplace agreement says otherwise it governs — but nothing here is hidden or reattributed.',
      },
    ],
  },
  {
    id: 'agencies',
    name: 'Agencies and freelancers',
    searchPhrase: 'add client reviews to a Webflow or Wix site',
    sources: ['google', 'trustpilot'],
    problem:
      'You build the site, the client asks for their reviews on it, and you end up writing a component you then have to maintain for every client you have.',
    outcome:
      'One widget you paste, per client, that keeps itself up to date — and looks the same on Wix, Webflow, WordPress and Squarespace.',
    widget: 'quote',
    widgetReason:
      'A single review set into a designed page, so social proof does not fight the layout you just built.',
    seoTitle: 'Add client reviews to a Webflow or Wix site — for agencies',
    seoDescription:
      'Build the reviews section once and paste it into every client site. Works on Wix, Webflow, WordPress and Squarespace, every layout on the free plan, no per-client licence.',
    headline: 'Stop building the reviews section again.',
    markPhrase: 'again',
    intro:
      'Every client site needs one, none of them needs a bespoke one, and the component you wrote for the last project is now something you maintain. ReviewsJet is the part you paste instead of the part you own.',
    points: [
      {
        title: 'Same widget on every platform you build on',
        body: 'A Wix build, a Webflow build and a WordPress build normally mean three different answers to the same request. This is one answer: an app on Wix and Webflow, a plugin on WordPress, a snippet everywhere else.',
      },
      {
        title: 'The client updates it, not you',
        body: 'Reviews are the section clients ask to change most and the one you least want to be on the hook for. They publish and unpublish from their own dashboard; you never get the email.',
      },
      {
        title: 'Free plan is a real plan',
        body: 'Every layout works on free, including the ones people expect to pay for. You can finish a client site before anybody has agreed a budget for it, which is usually the order these things actually happen in.',
      },
    ],
    faqs: [
      {
        q: 'Do I need an account per client?',
        a: 'Yes — one workspace per client, because their reviews and their billing are theirs. That is also what lets you hand the whole thing over when the project ends rather than remaining the account holder for a site you no longer touch.',
      },
      {
        q: 'Is there white labelling?',
        a: 'No. Paid plans remove the \\u201cPowered by ReviewsJet\\u201d line from the widget, which is what most agencies mean by the question, but there is no agency dashboard, no client portal and no reseller programme. We would rather say that plainly than let you find out after the pitch.',
      },
      {
        q: 'What if the client is on Shopify?',
        a: 'ReviewsJet does not support Shopify. That is the one platform where the answer is no, and it is better to know now.',
      },
    ],
  },
  {
    id: 'saas',
    name: 'Software, SaaS and apps',
    searchPhrase: 'show App Store and Google Play reviews on a SaaS website',
    sources: ['appstore', 'googleplay', 'trustpilot', 'google'],
    problem:
      'The reviews sit in the App Store, Google Play and on Trustpilot, and the pages where somebody decides to sign up or pay — the homepage, the pricing page — have nothing on them but claims.',
    outcome:
      'Real users rating the product, pulled straight from the stores, on the page where the decision to pay is being made.',
    widget: 'quote',
    widgetReason:
      'One quotation beside a price is more persuasive than a wall of them, and it does not compete with the plan cards.',
    seoTitle: 'Show App Store and Google Play reviews on your SaaS site',
    seoDescription:
      'Import your app reviews from the App Store and Google Play, plus Trustpilot and Google, choose which ones appear, and put them on your homepage and pricing page with one line of code.',
    headline: 'Your users already reviewed you. In the app stores.',
    markPhrase: 'In the app stores',
    intro:
      'Thousands of iPhone and Android users have rated your product, and every one of those reviews is locked inside a store page. Your website — where people decide whether to sign up — shows three plan cards and a testimonial you wrote yourself.',
    points: [
      {
        title: 'Both stores, one library',
        body: 'Paste your App Store and Google Play links and the reviews land side by side with your Trustpilot ones. Connect several App Store countries if your users are spread out — each storefront has its own reviews.',
      },
      {
        title: 'One quote beats a wall, on pricing',
        body: 'On a pricing page a grid of reviews competes with the plan cards for the same decision. A single store review set into the page — the quote widget — supports the price instead of arguing with it. Save the wall for the homepage.',
      },
      {
        title: 'Verified, and visibly so',
        body: 'Each card carries the reviewer\'s name, the date and the App Store or Google Play mark. That is a different claim from a testimonial typed into your CMS, and the difference is the entire reason to do this rather than write three quotes yourself.',
      },
    ],
    faqs: [
      {
        q: 'Do new app reviews show up on their own?',
        a: 'Yes. ReviewsJet re-checks your store pages and brings new reviews in as users leave them — after a release, the reviews of the new version arrive without you pasting anything again. You still choose which ones are published.',
      },
      {
        q: 'Our users are in several countries. Which reviews come in?',
        a: 'The ones from the store in your link. On the App Store that is the country in the address (apps.apple.com/us/…, /gb/…); on Google Play it is the language and country in the link. Connect each one you care about and they share one library.',
      },
      {
        q: 'We collect NPS and testimonials ourselves. Can I use those?',
        a: 'Yes — ReviewsJet has its own collection forms and review request emails, and reviews collected that way sit in the same library as your store reviews. They carry a verified mark because a named person filled in a form at an address you do not control; a review you type in yourself does not, and should not.',
      },
      {
        q: 'What about G2 or Capterra?',
        a: 'Not supported. ReviewsJet reads the App Store, Google Play, Google, Trustpilot, Yelp, TripAdvisor and Airbnb directly, and Etsy, Amazon, AliExpress and TourRadar through the extension. If your reviews are on G2, this is not the tool for that half of them.',
      },
    ],
  },
  {
    id: 'clinics',
    name: 'Clinics and practices',
    searchPhrase: 'show patient reviews on a dental or clinic website',
    sources: ['google'],
    problem:
      'Patients check Google before booking and then never look at it again — your own site, which is where they book, says nothing about what other patients thought.',
    outcome:
      'Reviews on the booking page, so the reassurance arrives at the moment somebody is deciding to pick up the phone.',
    widget: 'list',
    widgetReason:
      'Reviews of a practice are long and specific; a single column keeps them readable rather than clipping them into cards.',
    seoTitle: 'Show patient reviews on a dental or clinic website',
    seoDescription:
      'Dentists, physios, opticians and private practices: bring your Google reviews onto the page where patients actually book, and keep control of which ones appear.',
    headline: 'Patients check Google. Then they book on your site.',
    markPhrase: 'Then they book on your site',
    intro:
      'The reassurance happens on one page and the booking happens on another, and the booking page is the one that says nothing about what other patients thought. That gap is the whole of the problem.',
    points: [
      {
        title: 'Reviews where the phone number is',
        body: 'Somebody who has already read your Google reviews and arrived at your booking page should not have to remember them. Put the reviews on the page with the appointment form and the reassurance arrives at the moment it is needed.',
      },
      {
        title: 'Long reviews, read properly',
        body: 'Reviews of a practice are specific and long — a nervous patient, a procedure that went well, a receptionist who was kind. The list layout gives each one a full column instead of clipping it into a card.',
      },
      {
        title: 'Nothing goes live on its own',
        body: 'Every review arrives unpublished. In a regulated setting, a widget that could put a patient\'s words on your site without you seeing them first is not something you would install, so it does not work that way.',
      },
    ],
    faqs: [
      {
        q: 'Are there rules about advertising patient testimonials?',
        a: 'In many places, yes — the UK\'s GDC, for instance, has guidance on how patient reviews may be used, and rules differ by profession and country. ReviewsJet gives you the control to comply (nothing publishes without you, and you can unpublish at any time) but it cannot tell you what your regulator allows. Check that first.',
      },
      {
        q: 'Can a patient ask for their review to be taken down?',
        a: 'Unpublish it and it leaves your site immediately; delete it and it is gone from the library. The review stays on Google, because that is theirs and Google\'s, not ours.',
      },
      {
        q: 'We have several practitioners. Can each have their own reviews?',
        a: 'Tag reviews by practitioner and limit each profile page\'s widget to that tag. It is manual — the reviews do not know who they are about — but it takes a moment per review and the result is a proper profile page.',
      },
    ],
  },
  {
    id: 'property',
    name: 'Estate and letting agents',
    searchPhrase: 'add Google reviews to an estate agent website',
    sources: ['google', 'trustpilot'],
    problem:
      'You have reviews per branch, and a website that treats the business as one thing — so a seller in one town reads reviews about another.',
    outcome:
      'Each branch page showing its own branch’s reviews, from its own Google listing, without maintaining three separate sites.',
    widget: 'grid',
    widgetReason:
      'A tidy block per branch page, and widgets can be limited by tag so each page shows only its own.',
    seoTitle: 'Add Google reviews to an estate agent website',
    seoDescription:
      'Estate and letting agents: show each branch\'s own Google reviews on its own page, from its own listing, without running three separate websites.',
    headline: 'A seller in one town does not care about your other branch.',
    markPhrase: 'does not care about your other branch',
    intro:
      'You have reviews per branch and a website that treats the firm as one thing. So the vendor deciding whether to instruct you reads about an office forty miles away, or an average that is nobody\'s experience.',
    points: [
      {
        title: 'A listing per branch, kept apart',
        body: 'Connect each branch\'s Google profile as its own source. They arrive in one library, still labelled, and a widget limited to a branch tag shows only that branch\'s reviews on that branch\'s page.',
      },
      {
        title: 'Sellers and landlords are different audiences',
        body: 'A review about a smooth completion and one about a responsive lettings team are aimed at different people. Tag them and the sales page and the lettings page stop showing each other\'s proof.',
      },
      {
        title: 'Instructions turn on trust',
        body: 'Choosing an agent is choosing who to trust with the largest transaction most people make. Portals show your properties; your own site is the only place you get to show what your clients thought of you.',
      },
    ],
    faqs: [
      {
        q: 'Can I connect more than one Google listing?',
        a: 'Yes, and multi-branch firms are the main reason that exists. A source in ReviewsJet is a listing, not a platform, so three branches is three Google sources with names you choose.',
      },
      {
        q: 'How many sources can I have?',
        a: 'One on the free plan, two on Professional and unlimited on Advanced — so a firm with more than two branches is on Advanced. Worth knowing before you start rather than after.',
      },
      {
        q: 'Do reviews update when a branch gets a new one?',
        a: 'On Advanced, yes, automatically each day. On the other plans you refresh a source when you want to, which for most branches is a job somebody does once a month.',
      },
    ],
  },
  {
    id: 'tours',
    name: 'Tours, activities and events',
    searchPhrase: 'put Tripadvisor reviews on a tour operator website',
    sources: ['tripadvisor', 'google', 'tourradar'],
    problem:
      'Bookings come through operators who keep the reviews, so the direct-booking site — the one that does not pay commission — is the one with no proof on it.',
    outcome:
      'The reviews from every operator you sell through, on the page where somebody can book without one.',
    widget: 'masonry',
    widgetReason:
      'Activity reviews come with photographs, and masonry is the layout that sizes itself around them.',
    seoTitle: 'Put TripAdvisor reviews on a tour operator website',
    seoDescription:
      'Tour and activity operators: show the reviews you earned through OTAs on the site where a direct booking pays no commission. TripAdvisor, Google and TourRadar in one widget.',
    headline: 'The commission-free booking has the least proof on it.',
    markPhrase: 'the least proof on it',
    intro:
      'Your reviews accumulate wherever the booking was made — TripAdvisor, GetYourGuide, TourRadar — and every one of those pays a commission. Your own site keeps the full fare and shows a traveller nothing.',
    points: [
      {
        title: 'Every operator\'s reviews, on your page',
        body: 'Bring TripAdvisor, Google and TourRadar together so the direct-booking page carries more proof than any single OTA listing does. That is an argument for booking with you that the OTAs structurally cannot make.',
      },
      {
        title: 'Photographs sell activities',
        body: 'Traveller reviews come with pictures of the place, and those pictures do the persuading. Masonry sizes each card around its photo rather than cropping everything to a uniform box.',
      },
      {
        title: 'One tour, one page, one set of reviews',
        body: 'A hiking week and a city food tour attract different people. Tag reviews per trip and each tour\'s page shows its own, which is what makes a tour page read as being about that tour.',
      },
    ],
    faqs: [
      {
        q: 'TourRadar is listed as an extension source. What does that mean?',
        a: 'TourRadar\'s review pages cannot be read from a server, so the free ReviewsJet Chrome extension collects them in your own browser while you are signed in. TripAdvisor and Google are read directly and refresh on their own; TourRadar arrives when you run the extension.',
      },
      {
        q: 'Can I show the reviews on a booking engine I do not control?',
        a: 'Only if it lets you paste HTML, and many do not. The reviews go on the pages of your own site that lead into it — the tour page, the homepage — which is where the decision is made anyway.',
      },
      {
        q: 'Our season is six months long. Do old reviews look stale?',
        a: 'Each card shows its date, so a review from last August reads as last August rather than as pretending to be recent. For a seasonal business that is usually fine; if it is not, unpublish the oldest and keep the widget to the recent ones.',
      },
    ],
  },
  {
    id: 'professional',
    name: 'Professional services',
    searchPhrase: 'add client testimonials to an accountant or law firm website',
    sources: ['google', 'trustpilot'],
    problem:
      'Testimonials on a professional services site are usually three sentences somebody typed into the CMS in 2019, and they read exactly like that.',
    outcome:
      'Named, dated, verifiable reviews from a public platform — which is a different claim from a quotation you wrote yourself.',
    widget: 'list',
    widgetReason:
      'A column of full reviews with the source mark on each, so the reader can tell they were not written in-house.',
    seoTitle: 'Add client testimonials to an accountant or law firm website',
    seoDescription:
      'Accountants, solicitors, consultants and advisers: replace the testimonials somebody typed into the CMS in 2019 with named, dated reviews from Google and Trustpilot.',
    headline: 'A testimonial you wrote yourself persuades nobody.',
    markPhrase: 'persuades nobody',
    intro:
      'Most professional services sites carry three quotations with an initial and a town under them. Everybody reading has worked out that those were written in-house — which makes them worse than nothing, because they say you know proof is needed and could not find any.',
    points: [
      {
        title: 'Named, dated, and checkable',
        body: 'Every card carries the reviewer\'s name, when they wrote it and the platform mark. A reader who doubts it can go and look, and the fact that they *could* is what makes the review worth having.',
      },
      {
        title: 'Long-form reviews, in full',
        body: 'A client explaining how a difficult sale or a tax investigation was handled writes paragraphs, not lines. The list layout gives each review a full-width column instead of clipping it to fit a card.',
      },
      {
        title: 'You still choose',
        body: 'Publishing is yours. A review that names a matter you would rather not advertise stays unpublished — the control is complete, which is the only basis on which a professional firm would put this on a site at all.',
      },
    ],
    faqs: [
      {
        q: 'Our clients will not leave a public review. Is this any use?',
        a: 'Partly. ReviewsJet has its own collection forms, and a review left through one carries a verified mark because a named person filled it in at an address you do not control. It is not the same as a public Google review, and the site does not pretend it is — but it is a great deal better than a quotation typed into your CMS.',
      },
      {
        q: 'Can I reply to a review?',
        a: 'To reviews collected through ReviewsJet, yes. A Google review has to be replied to in Google — nobody else can post into that thread — so there is no reply box here pretending otherwise.',
      },
      {
        q: 'Is client confidentiality a problem?',
        a: 'It can be, and it is your judgement rather than ours. The relevant mechanic is that nothing publishes without you and anything published can be unpublished immediately, so a review that says more than it should never has to reach your site.',
      },
    ],
  },
];

export function industryById(id: string): IndustryContent | undefined {
  return INDUSTRIES.find((industry) => industry.id === id);
}

/*
  There is deliberately no `INDUSTRIES_PATH` constant.

  Every *generated* path on this site goes through a helper — `widgetPath`,
  `generatorPath`, `integrationPath` — because those are composed from content
  and a hand-written copy would drift. The fixed top-level pages do not:
  `/pricing`, `/agencies`, `/manage-reviews` and the rest appear as literals in
  the navigation, the footer and the sitemap.

  That is not just consistency. `links.test.ts` proves that every page in the
  sitemap is reachable from somewhere by scanning the source for a quoted path
  after `to:` or `href=` — a literal it can see. Routing this page via a constant
  made it invisible to that sweep, so the guard reported it as an orphan while
  it was in fact linked from two places. A constant that defeats the test which
  proves the link exists is worth less than the literal it replaced.
*/
